<script setup>
/**
 * Uma linha de opção do WhatsApp Baileys: rótulo, descrição, aviso e o controle à direita.
 */
import { computed, reactive, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { between, helpers, integer, required } from '@vuelidate/validators';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
    // Expected shape:
    // Textos já traduzidos (baileysConfigSections recebe o t).
    // { key: string, label: string, type: 'checkbox' | 'number', description?: string,
    //   hint?: string (efeito colateral, mostrado como aviso), placeholder?: string,
    //   min?: number, max?: number, invalid?: string (erro do número fora de min–max), hasMessage?: boolean,
    //   nested?: boolean (sub-opção: recuada sob a opção-pai) }
  },
  modelValue: {
    type: [Boolean, Number, String],
    default: false,
  },
  msgCallValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:msgCallValue',
  'change',
  'blurMsg',
  'blurNumber',
]);

// Rótulo clicável: liga o <label> ao switch/campo da linha.
const inputId = `baileys-${props.attribute.key}`;

// Local value for switch
const switchValue = ref(Boolean(props.modelValue));
const number = reactive({ value: props.modelValue });
const msgCallLocal = ref(props.msgCallValue);

// Watch for external changes
watch(
  () => props.modelValue,
  newVal => {
    if (props.attribute.type === 'checkbox') {
      switchValue.value = Boolean(newVal);
    } else {
      number.value = newVal;
    }
  }
);

watch(
  () => props.msgCallValue,
  newVal => {
    msgCallLocal.value = newVal;
  }
);

// Handle switch change
const handleSwitchChange = value => {
  emit('update:modelValue', value);
  emit('change', { key: props.attribute.key, value });
};

// Número (dias do histórico): fora de min–max mostra o erro e não salva, em vez de trocar o valor calado.
const numberRules = computed(() => {
  if (props.attribute.type !== 'number') return {};
  const { min, max, invalid } = props.attribute;
  const rule = validator => helpers.withMessage(invalid, validator);
  return {
    value: {
      required: rule(required),
      integer: rule(integer),
      between: rule(between(min, max)),
    },
  };
});
const v$ = useVuelidate(numberRules, number);
const numberError = computed(() => v$.value.value?.$errors[0]?.$message);

const handleNumberChange = async () => {
  if (!(await v$.value.$validate())) return;
  emit('update:modelValue', number.value);
  emit('blurNumber', { key: props.attribute.key, value: number.value });
};

// Handle message blur
const handleMsgBlur = () => {
  emit('update:msgCallValue', msgCallLocal.value);
  emit('blurMsg', { key: props.attribute.key, value: msgCallLocal.value });
};
</script>

<template>
  <div
    class="py-3"
    :class="{
      'ltr:ml-1 rtl:mr-1 ltr:pl-4 rtl:pr-4 border-n-weak ltr:border-l rtl:border-r !border-t-0 !py-2':
        attribute.nested,
    }"
  >
    <div class="flex items-start justify-between gap-6">
      <label
        :for="inputId"
        class="min-w-0 flex flex-col gap-0.5 cursor-pointer"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ attribute.label }}
        </span>
        <span v-if="attribute.description" class="text-sm text-n-slate-11">
          {{ attribute.description }}
        </span>
        <span
          v-if="attribute.hint"
          class="flex items-center gap-1.5 text-sm text-n-amber-11"
        >
          <span
            class="i-lucide-triangle-alert size-3.5 flex-shrink-0"
            aria-hidden="true"
          />
          {{ attribute.hint }}
        </span>
      </label>

      <Switch
        v-if="attribute.type === 'checkbox'"
        :id="inputId"
        v-model="switchValue"
        class="mt-1"
        :disabled="disabled"
        @change="handleSwitchChange"
      />
      <input
        v-else-if="attribute.type === 'number'"
        :id="inputId"
        v-model.number="number.value"
        type="number"
        :min="attribute.min"
        :max="attribute.max"
        :disabled="disabled"
        :aria-invalid="Boolean(numberError)"
        :aria-describedby="numberError ? `${inputId}-error` : undefined"
        class="!w-20 !h-8 !mb-0 text-sm text-center flex-shrink-0"
        :class="{ '!outline-n-ruby-8': numberError }"
        @change="handleNumberChange"
      />
    </div>
    <p
      v-if="numberError"
      :id="`${inputId}-error`"
      role="alert"
      class="mt-1 mb-0 text-sm text-n-ruby-11"
    >
      {{ numberError }}
    </p>

    <textarea
      v-if="attribute.hasMessage && switchValue"
      v-model="msgCallLocal"
      :placeholder="attribute.placeholder"
      :aria-label="attribute.label"
      :disabled="disabled"
      rows="2"
      class="!mt-3 !mb-0 !h-auto text-sm resize-none"
      @blur="handleMsgBlur"
    />
  </div>
</template>
